import moment, { Moment } from "moment";
import React, { useEffect, useRef, useState } from "react";

import {
  DateTimeRangePickerPreset,
  TimeRange,
} from "veneer/DateTimeRangePicker";

import {
  DataTableDateRangeFilter,
  DataTableFilterContainer,
  DataTableMultiSelectFilter,
} from "cortex/ui/src/ui/filter/DataTableFilters";
import type { Option } from "cortex/ui/src/ui/table/types";

export type EventFilterId =
  | "device"
  | "name"
  | "priority"
  | "source"
  | "tags"
  | "eventDate";

export type EventDatePreset = "lastHour" | "last24Hours" | "last7Days";

export interface EventTableFilterState {
  eventDate: TimeRange;
  eventDatePreset: EventDatePreset | null;
  name: ReadonlyArray<string>;
  priority: ReadonlyArray<string>;
  source: ReadonlyArray<string>;
  targets: ReadonlyArray<string>;
  visibleFilters: ReadonlyArray<EventFilterId>;
}

export interface EventFilterOptions {
  deviceOptions: ReadonlyArray<Option<string>>;
  eventNameOptions: ReadonlyArray<Option<string>>;
  priorityOptions: ReadonlyArray<Option<string>>;
  sourceOptions: ReadonlyArray<Option<string>>;
  tagOptions: ReadonlyArray<Option<string>>;
}

interface EventTableFiltersProps extends EventFilterOptions {
  onInteractionChange?: (isInteracting: boolean) => void;
  onChange: (value: EventTableFilterState) => void;
  value: EventTableFilterState;
}

const FILTER_IDS: ReadonlyArray<EventFilterId> = [
  "device",
  "name",
  "priority",
  "source",
  "tags",
  "eventDate",
];

export const createEventDatePresetRange = (
  preset: EventDatePreset,
): TimeRange => {
  const endDate = moment();

  switch (preset) {
    case "lastHour":
      return [endDate.clone().subtract(1, "hour"), endDate];
    case "last24Hours":
      return [endDate.clone().subtract(1, "day"), endDate];
    case "last7Days":
      return [endDate.clone().subtract(7, "days"), endDate];
  }
};

export const EVENT_DATE_PRESETS: ReadonlyArray<DateTimeRangePickerPreset> = [
  {
    id: "lastHour",
    label: "Last Hour",
    getValue: () => createEventDatePresetRange("lastHour"),
  },
  {
    id: "last24Hours",
    label: "Last 24 Hours",
    getValue: () => createEventDatePresetRange("last24Hours"),
  },
  {
    id: "last7Days",
    label: "Last 7 Days",
    getValue: () => createEventDatePresetRange("last7Days"),
  },
];

export const createDefaultEventDateRange = (): TimeRange =>
  createEventDatePresetRange("lastHour");

export const createDefaultEventTableFilters = (): EventTableFilterState => ({
  eventDate: createDefaultEventDateRange(),
  eventDatePreset: "lastHour",
  name: [],
  priority: ["1", "2"],
  source: [],
  targets: [],
  visibleFilters: ["priority", "eventDate"],
});

const parseStringArray = (value: unknown): ReadonlyArray<string> =>
  Array.isArray(value)
    ? value.filter((item): item is string => typeof item === "string")
    : [];

const parseDate = (value: unknown): Moment | null => {
  if (moment.isMoment(value)) {
    return value.isValid() ? value.clone() : null;
  }

  if (typeof value !== "string") {
    return null;
  }

  const parsed = moment(value);
  return parsed.isValid() ? parsed : null;
};

const parseTimeRange = (value: unknown): TimeRange =>
  Array.isArray(value)
    ? [parseDate(value[0]), parseDate(value[1])]
    : [null, null];

const parseEventDatePreset = (value: unknown): EventDatePreset | null => {
  if (
    value === "lastHour" ||
    value === "last24Hours" ||
    value === "last7Days"
  ) {
    return value;
  }

  return null;
};

const normalizeEventDate = (
  value: unknown,
  presetValue: unknown,
): Pick<EventTableFilterState, "eventDate" | "eventDatePreset"> => {
  const eventDatePreset = parseEventDatePreset(presetValue);
  if (eventDatePreset) {
    return {
      eventDate: createEventDatePresetRange(eventDatePreset),
      eventDatePreset,
    };
  }

  const eventDate = parseTimeRange(value);
  if (eventDate[0] && eventDate[1]) {
    return { eventDate, eventDatePreset: null };
  }

  return {
    eventDate: createDefaultEventDateRange(),
    eventDatePreset: "lastHour",
  };
};

export const normalizeEventTableFilters = (
  value: unknown,
): EventTableFilterState => {
  if (!value || typeof value !== "object") {
    return createDefaultEventTableFilters();
  }

  const filters = value as Record<string, unknown>;
  const parsedVisibleFilters = parseStringArray(filters.visibleFilters).filter(
    (id): id is EventFilterId => FILTER_IDS.includes(id as EventFilterId),
  );
  const visibleFilters = parsedVisibleFilters.includes("eventDate")
    ? parsedVisibleFilters
    : [...parsedVisibleFilters, "eventDate" as const];
  const eventDate = normalizeEventDate(
    filters.eventDate,
    filters.eventDatePreset,
  );

  return {
    ...eventDate,
    name: parseStringArray(filters.name),
    priority: parseStringArray(filters.priority),
    source: parseStringArray(filters.source),
    targets: parseStringArray(filters.targets),
    visibleFilters,
  };
};

const loadPersistedFilters = (storageKey: string): EventTableFilterState => {
  const saved = sessionStorage.getItem(storageKey);
  if (!saved) {
    return createDefaultEventTableFilters();
  }

  try {
    const parsed = JSON.parse(saved) as { filters?: unknown };
    return normalizeEventTableFilters(parsed.filters);
  } catch {
    return createDefaultEventTableFilters();
  }
};

export function usePersistedEventTableFilters(storageKey: string) {
  const [filters, setFilters] = useState<EventTableFilterState>(() =>
    loadPersistedFilters(storageKey),
  );
  const storageKeyRef = useRef(storageKey);

  useEffect(() => {
    if (storageKeyRef.current !== storageKey) {
      storageKeyRef.current = storageKey;
      setFilters(loadPersistedFilters(storageKey));
      return;
    }

    sessionStorage.setItem(storageKey, JSON.stringify({ filters }));
  }, [filters, storageKey]);

  return { filters, setFilters };
}

const getSelectedOptions = (
  selectedValues: ReadonlyArray<string>,
  options: ReadonlyArray<Option<string>>,
): ReadonlyArray<Option<string>> =>
  selectedValues.map(
    (selectedValue) =>
      options.find(({ value }) => String(value) === selectedValue) ?? {
        label: selectedValue,
        value: selectedValue,
      },
  );

export default function EventTableFilters({
  deviceOptions,
  eventNameOptions,
  onChange,
  onInteractionChange,
  priorityOptions,
  sourceOptions,
  tagOptions,
  value,
}: EventTableFiltersProps) {
  const renderMultiSelect = (
    id: "name" | "priority" | "source",
    label: string,
    options: ReadonlyArray<Option<string>>,
  ) => (
    <DataTableMultiSelectFilter
      dataTestId={`events-filter-${id}`}
      isOptionEqualToValue={(option, selected) =>
        String(option.value) === String(selected.value)
      }
      label={label}
      onChange={(selected) =>
        onChange({
          ...value,
          [id]: selected.map(({ value: selectedValue }) =>
            String(selectedValue),
          ),
        })
      }
      onInteractionChange={onInteractionChange}
      options={options}
      value={getSelectedOptions(value[id], options)}
    />
  );

  const renderTargetMultiSelect = (
    id: "device" | "tags",
    label: string,
    options: ReadonlyArray<Option<string>>,
    targetPrefix: "d_" | "t_",
  ) => (
    <DataTableMultiSelectFilter
      dataTestId={`events-filter-${id}`}
      isOptionEqualToValue={(option, selected) =>
        String(option.value) === String(selected.value)
      }
      label={label}
      onChange={(selected) =>
        onChange({
          ...value,
          targets: [
            ...value.targets.filter(
              (target) => !target.startsWith(targetPrefix),
            ),
            ...selected.map(({ value: selectedValue }) =>
              String(selectedValue),
            ),
          ],
        })
      }
      onInteractionChange={onInteractionChange}
      options={options}
      value={getSelectedOptions(
        value.targets.filter((target) => target.startsWith(targetPrefix)),
        options,
      )}
    />
  );

  return (
    <DataTableFilterContainer>
      {value.visibleFilters.includes("eventDate") && (
        <DataTableDateRangeFilter
          dataTestId="events-filter-event-date"
          disableClearable
          label="Time Range"
          onChange={(eventDate) =>
            onChange({ ...value, eventDate, eventDatePreset: null })
          }
          onInteractionChange={onInteractionChange}
          onPresetChange={(eventDate, preset) =>
            onChange({
              ...value,
              eventDate,
              eventDatePreset: parseEventDatePreset(preset.id),
            })
          }
          presets={EVENT_DATE_PRESETS}
          selectedPresetId={value.eventDatePreset}
          value={value.eventDate}
        />
      )}
      {value.visibleFilters.includes("device") &&
        renderTargetMultiSelect("device", "Device", deviceOptions, "d_")}
      {value.visibleFilters.includes("name") &&
        renderMultiSelect("name", "Name", eventNameOptions)}
      {value.visibleFilters.includes("priority") &&
        renderMultiSelect("priority", "Priority", priorityOptions)}
      {value.visibleFilters.includes("source") &&
        renderMultiSelect("source", "Source", sourceOptions)}
      {value.visibleFilters.includes("tags") &&
        renderTargetMultiSelect("tags", "Tags", tagOptions, "t_")}
    </DataTableFilterContainer>
  );
}
